import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";

const compareDocTypes = (dt1, dt2) => dt1.internalid === dt2.internalid;

const docTypeSlice = createSlice({
    name: 'docTypes',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const dt of ensureArray(action.payload)) {
                const idx = state.data.findIndex(dt2 => compareDocTypes(dt, dt2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, dt);
                } else {
                    state.data.push(dt);
                }
            }
        },
        remove: (state, action) => {
            for (const dt of ensureArray(action.payload)) {
                let idx = state.data.findIndex(dt2 => compareDocTypes(dt2, dt));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => docTypeSlice.getInitialState());
    }
})

// #region network operations
const fetchDocTypes = network => network.post('/api/v1/doctypes', {}, docTypeSlice.actions.set);
export const createDocType = (network, doctype) => network.post('/api/v1/doctype', doctype, docTypeSlice.actions.addOrUpdate);
export const removeDocType = (network, doctype) => network.delete('/api/v1/doctype/' + encodeURIComponent(doctype.internalid), docTypeSlice.actions.remove);
// #endregion

// #region permission checks
export const canInsertDocType = (credentials, doctype) => credentials.access.hasFull('administrator');
export const canUpdateDocType = (credentials, doctype) => credentials.access.hasFull('administrator');
export const canRemoveDocType = (credentials, doctype) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectAllDocTypes = state => state.docTypes.data;
const makeSelectForSiteLevelType = createSelector([selectAllDocTypes, (state, site) => site, (state, site, level) => level, (state, site, level, type) => type], (doctypes, site, level, type) => {
    return doctypes.filter(dt => (!dt.site || !site || dt.site === site) && (!dt.level || !level || dt.level === level) && (!dt.type || !type || dt.type === type))
});
const selectDocTypes = (site, level, type) => state => makeSelectForSiteLevelType(state, site, level, type);
// #endregion

// #region listeners
const listen = {
    doctypeCreated: (doctype, { dispatch }) => dispatch(docTypeSlice.actions.addOrUpdate(doctype)),
    doctypeRemoved: (doctype, { dispatch }) => dispatch(docTypeSlice.actions.remove(doctype))
}
// #endregion

export const [useAllDocTypes] = dataHookFactory(selectAllDocTypes, { run: fetchDocTypes, listen });
export const [useDocTypes] = dataHookFactory(selectDocTypes, (site, level, type) => ({ run: fetchDocTypes, listen }));

const reducerDetails = { name: docTypeSlice.name, reducer: docTypeSlice.reducer };
export default reducerDetails;